<template>
  <v-menu
    v-model="menuVisible"
    :close-on-content-click="true"
    location="bottom"
  >
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        :variant="variant"
        :color="color"
        :density="density"
        :size="size"
        append-icon="mdi-share-variant-outline"
        class="share-button"
      >
        Share
      </v-btn>
    </template>

    <v-list class="share-menu" min-width="180">
      <v-list-item
        v-for="platform in platforms"
        :key="platform.name"
        @click="shareOn(platform.name)"
        class="share-menu-item"
      >
        <template v-slot:prepend>
          <v-icon :icon="platform.icon" :color="platform.color" size="small" />
        </template>
        <v-list-item-title>{{ platform.label }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';

  interface Props {
    title?: string;
    description?: string;
    variant?: 'text' | 'flat' | 'elevated' | 'tonal' | 'outlined' | 'plain';
    color?: string;
    density?: 'default' | 'comfortable' | 'compact';
    size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large';
    reportSlug?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    title: 'EdReports',
    description: 'Check out this content from EdReports',
    variant: 'outlined',
    color: 'primary',
    density: 'default',
    size: 'default',
    reportSlug: undefined,
  });

  const menuVisible = ref(false);

  const platforms = computed(() => [
    {
      name: 'facebook',
      label: 'Facebook',
      icon: 'mdi-facebook',
      color: '#1877F2',
    },
    {
      name: 'twitter',
      label: 'X (Twitter)',
      icon: 'mdi-twitter',
      color: '#000000',
    },
    {
      name: 'linkedin',
      label: 'LinkedIn',
      icon: 'mdi-linkedin',
      color: '#0A66C2',
    },
    {
      name: 'email',
      label: 'Email',
      icon: 'mdi-email',
      color: '#666666',
    },
  ]);

  const shareOn = (platform: string) => {
    const fullUrl = window.location.href;
    const title = props.title || 'EdReports';
    const description =
      props.description || 'Check out this content from EdReports';
    const host = window.location.protocol + '//' + window.location.hostname;

    let shareUrl = fullUrl;
    if (
      props.reportSlug &&
      (platform === 'linkedin' || platform === 'twitter')
    ) {
      shareUrl = `${host}/reports/${props.reportSlug}`;
    }

    let shareLink = '';
    let windowFeatures = 'width=600,height=400,scrollbars=yes,resizable=yes';

    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`;
        window.open(shareLink, '_blank', windowFeatures);
        break;

      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`;
        window.open(shareLink, '_blank', windowFeatures);
        break;

      case 'linkedin':
        shareLink = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`;
        window.open(shareLink, '_blank', windowFeatures);
        break;

      case 'email':
        shareLink = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(fullUrl)}`;
        window.open(shareLink);
        break;
    }

    menuVisible.value = false;
  };
</script>

<style scoped>
  .share-button {
    text-transform: none !important;
  }

  .share-menu {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .share-menu-item {
    padding: 12px 16px;
    transition: background-color 0.2s ease;
  }

  .share-menu-item:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
</style>
